import { Injectable, OnDestroy, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { DisplayState } from '../models/display-state';
import { LayerRenderData, LayerStyle, LayerType, LayerPosition } from '../models/layer-render-data';
import { LayoutStyleType } from '../models/layout-style-type';
import { ConnectionState, ContainerStyle, RenderPayload } from '../models/render-payload';

@Injectable()
export class DisplaySignalRService implements OnDestroy {
    private connection: signalR.HubConnection | null = null;
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private expiryTimer: ReturnType<typeof setTimeout> | null = null;
    private currentDisplayId = '';
    private currentPayload: RenderPayload | null = null;
    private expiryDeadline: number | null = null;

    readonly connectionState = signal<ConnectionState>('disconnected');
    readonly renderPayload$ = new Subject<RenderPayload>();

    connect(displayId: string, token?: string): void {
        if (this.connection) {
            this.disconnect();
        }

        this.currentDisplayId = displayId;

        const urlOptions: signalR.IHttpConnectionOptions = token
            ? { accessTokenFactory: () => token }
            : {};

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(environment.displayHubUrl, urlOptions)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    const delays = [1000, 2000, 5000, 10000, 30000];
                    return delays[Math.min(retryContext.previousRetryCount, delays.length - 1)];
                },
            })
            .configureLogging(
                environment.production ? signalR.LogLevel.Warning : signalR.LogLevel.Information
            )
            .build();

        this.connection.on('RenderStateChanged', (payload: unknown) => {
            this.emitPayload(this.normalizeRenderPayload(payload));
        });

        this.connection.on('RenderState', (payload: unknown) => {
            this.emitPayload(this.normalizeRenderPayload(payload));
        });

        this.connection.onreconnecting(() => {
            this.connectionState.set('reconnecting');
            this.stopHeartbeat();
        });

        this.connection.onreconnected(() => {
            this.connectionState.set('connected');
            this.joinDisplay();
            this.startHeartbeat();
        });

        this.connection.onclose(() => {
            this.connectionState.set('disconnected');
            this.stopHeartbeat();
        });

        this.connectionState.set('connecting');
        this.connection
            .start()
            .then(() => {
                this.connectionState.set('connected');
                this.joinDisplay();
                this.startHeartbeat();
            })
            .catch(() => {
                this.connectionState.set('disconnected');
            });
    }

    disconnect(): void {
        this.leaveDisplay(this.currentDisplayId);
        this.stopHeartbeat();
        this.clearExpiryTimer();
        this.currentPayload = null;
        this.expiryDeadline = null;
        this.connection?.stop();
        this.connection = null;
        this.connectionState.set('disconnected');
    }

    rejoinDisplay(displayId: string): void {
        const previousDisplayId = this.currentDisplayId;
        this.currentDisplayId = displayId;

        if (previousDisplayId && previousDisplayId !== displayId) {
            this.leaveDisplay(previousDisplayId);
        }

        this.joinDisplay();
    }

    private emitPayload(payload: RenderPayload): void {
        this.currentPayload = payload;
        this.renderPayload$.next(payload);
        this.scheduleExpiry(payload);
    }

    // The server only reverts an expired state when asked, and the 30s heartbeat was the only asker:
    // a plate lingered on the sign for ~14s past its TTL (up to 99s). Ask once, exactly at expiry.
    // Idle never reverts, and a newer payload replaces the pending timer, so this is at most one
    // request per non-Idle state — the same revert that would have happened anyway, just on time.
    //
    // The delay is the payload's own TTL (expiresAt − pushedAt, both server timestamps) counted from
    // when it arrived here — never expiresAt − Date.now(). A sign whose clock is behind would otherwise
    // wait out the skew too: an hour behind, and DriveAuthorized / ExitFree stay up for an hour.
    private scheduleExpiry(payload: RenderPayload): void {
        this.clearExpiryTimer();
        this.expiryDeadline = null;

        const ttlMs = this.ttlMs(payload);
        if (payload.state === DisplayState.Idle || ttlMs === null) {
            return;
        }

        this.expiryDeadline = performance.now() + ttlMs;

        // Small margin: the server compares against its own clock and a call that lands a hair early is a no-op.
        const delayMs = ttlMs + 250;
        const displayId = this.currentDisplayId;

        this.expiryTimer = setTimeout(() => {
            this.expiryTimer = null;
            this.expireRenderedState(displayId, payload);
        }, delayMs);
    }

    private ttlMs(payload: RenderPayload): number | null {
        if (!payload.expiresAt) {
            return null;
        }

        const ttlMs = Date.parse(payload.expiresAt) - Date.parse(payload.pushedAt);
        return Number.isNaN(ttlMs) ? null : Math.max(0, ttlMs);
    }

    // Names the payload being expired. The backend runs several replicas and only the one that pushed a
    // state has it cached, so the server cannot tell on its own what this sign is showing — this sign can.
    private expireRenderedState(displayId: string, payload: RenderPayload): void {
        if (this.connection?.state !== signalR.HubConnectionState.Connected || !payload.expiresAt) {
            return;
        }

        this.connection
            .invoke('ExpireRenderedState', displayId, payload.pushedAt, payload.expiresAt)
            .catch((err) => console.error('[DisplaySignalR] ExpireRenderedState failed', err));
    }

    // Backstop for an expiry the timer could not deliver (the connection was down when it fired, or the
    // server declined because it arrived a hair early): a state still on screen past its deadline is
    // asked about again on the next heartbeat. Same clock-free deadline as the timer.
    private expireIfOverdue(): void {
        const payload = this.currentPayload;
        if (!payload || payload.state === DisplayState.Idle || this.expiryDeadline === null || this.expiryTimer !== null) {
            return;
        }

        if (performance.now() >= this.expiryDeadline) {
            this.expireRenderedState(this.currentDisplayId, payload);
        }
    }

    private clearExpiryTimer(): void {
        if (this.expiryTimer !== null) {
            clearTimeout(this.expiryTimer);
            this.expiryTimer = null;
        }
    }

    private joinDisplay(): void {
        this.connection
            ?.invoke('JoinDisplay', this.currentDisplayId)
            .catch((err) => console.error('[DisplaySignalR] JoinDisplay failed', err));
    }

    private leaveDisplay(displayId: string): void {
        if (!displayId) {
            return;
        }

        this.connection
            ?.invoke('LeaveDisplay', displayId)
            .catch((err) => console.error('[DisplaySignalR] LeaveDisplay failed', err));
    }

    private normalizeRenderPayload(payload: unknown): RenderPayload {
        const source = this.asRecord(payload);
        const rawLayers = Array.isArray(source['layers']) ? source['layers'] : [];

        return {
            state: this.normalizeDisplayState(source['state']),
            layers: rawLayers.map((layer) => this.normalizeLayer(layer)),
            pushedAt: typeof source['pushedAt'] === 'string' ? source['pushedAt'] : new Date().toISOString(),
            expiresAt: typeof source['expiresAt'] === 'string' ? source['expiresAt'] : null,
            fallbackImageUrl:
                typeof source['fallbackImageUrl'] === 'string'
                    ? source['fallbackImageUrl']
                    : null,
            layoutStyle: this.normalizeLayoutStyle(source['layoutStyleType']),
            containerStyle: this.normalizeContainerStyle(source['containerStyle']),
        };
    }

    private normalizeLayoutStyle(value: unknown): LayoutStyleType | undefined {
        return value === LayoutStyleType.Relative || value === LayoutStyleType.Stack
            ? value
            : undefined;
    }

    private normalizeContainerStyle(value: unknown): ContainerStyle | null {
        const source = this.asNullableRecord(value);
        if (!source) {
            return null;
        }

        return {
            backgroundColor: typeof source['backgroundColor'] === 'string' ? source['backgroundColor'] : null,
            fontFamily: typeof source['fontFamily'] === 'string' ? source['fontFamily'] : null,
            color: typeof source['color'] === 'string' ? source['color'] : null,
            padding: typeof source['padding'] === 'string' ? source['padding'] : null,
        };
    }

    private normalizeLayer(layer: unknown): LayerRenderData {
        const source = this.asRecord(layer);

        return {
            layerKey: typeof source['layerKey'] === 'string' ? source['layerKey'] : null,
            layerType: this.normalizeLayerType(source['layerType'] ?? source['type']),
            zIndex: typeof source['zIndex'] === 'number' ? source['zIndex'] : 0,
            imageUrl: typeof source['imageUrl'] === 'string' ? source['imageUrl'] : null,
            textContent: typeof source['textContent'] === 'string'
                ? source['textContent']
                : typeof source['text'] === 'string'
                    ? source['text']
                    : null,
            position: this.normalizePosition(source['position'] ?? source['componentPosition']),
            style: this.normalizeStyle(source['style']),
            componentType: typeof source['componentType'] === 'string' ? source['componentType'] : null,
            componentData: this.asNullableRecord(source['componentData']),
        };
    }

    private normalizeDisplayState(value: unknown): RenderPayload['state'] {
        return typeof value === 'string' ? (value as RenderPayload['state']) : DisplayState.Idle;
    }

    private normalizeLayerType(value: unknown): LayerType {
        if (value === LayerType.TextOverlay || value === LayerType.ComponentWidget) {
            return value;
        }

        return LayerType.BackgroundImage;
    }

    private normalizePosition(value: unknown): LayerPosition | undefined {
        const source = this.asNullableRecord(value);
        if (!source) {
            return undefined;
        }

        return {
            top: typeof source['top'] === 'string' ? source['top'] : null,
            left: typeof source['left'] === 'string' ? source['left'] : null,
            right: typeof source['right'] === 'string' ? source['right'] : null,
            bottom: typeof source['bottom'] === 'string' ? source['bottom'] : null,
            width: typeof source['width'] === 'string' ? source['width'] : null,
            height: typeof source['height'] === 'string' ? source['height'] : null,
        };
    }

    private normalizeStyle(value: unknown): LayerStyle | undefined {
        const source = this.asNullableRecord(value);
        if (!source) {
            return undefined;
        }

        return {
            fontSize: typeof source['fontSize'] === 'string' ? source['fontSize'] : null,
            color: typeof source['color'] === 'string' ? source['color'] : null,
            fontWeight: typeof source['fontWeight'] === 'string' ? source['fontWeight'] : null,
            textAlign: typeof source['textAlign'] === 'string' ? source['textAlign'] : null,
            fontFamily: typeof source['fontFamily'] === 'string' ? source['fontFamily'] : null,
            backgroundColor: typeof source['backgroundColor'] === 'string' ? source['backgroundColor'] : null,
        };
    }

    private asRecord(value: unknown): Record<string, unknown> {
        return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
    }

    private asNullableRecord(value: unknown): Record<string, unknown> | null {
        return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
    }

    private startHeartbeat(): void {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            if (this.connection?.state === signalR.HubConnectionState.Connected) {
                this.connection
                    .invoke('Heartbeat', this.currentDisplayId)
                    .catch((err) => console.error('[DisplaySignalR] Heartbeat failed', err));
                this.expireIfOverdue();
            }
        }, environment.heartbeatIntervalMs);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer !== null) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    ngOnDestroy(): void {
        this.disconnect();
    }
}
